import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
  getCollectionBuildErrorMessage,
  getCollectionBuildSilhouettesCatalog,
} from "../../app/api/collectionBuild";
import { resolveImageGeneratorResultSrc } from "../../app/api/imageGenerator";
import { uploadImage } from "../../app/api/upload";
import {
  FIT_OPTIONS,
  NECKLINE_OPTIONS,
  SLEEVE_OPTIONS,
  WASH_OPTIONS,
  materialsOptionsFor,
  styleOptionsFor,
} from "../../constants/imageGeneratorDictionaries";
import {
  getFileName,
  getImageLink,
  getKeyForS3GenerativeFeature,
} from "../../utils/commonUtils";
import { ReactComponent as DropdownArrow } from "../../assets/icons/dropdownArrow.svg";
import CollectionBuildDenimSwatchSelect, {
  DenimSwatchOption,
} from "./CollectionBuildDenimSwatchSelect";
import CollectionBuildEmbroideryType from "./CollectionBuildEmbroideryType";
import CollectionBuildSelect from "./CollectionBuildSelect";
import CollectionBuildSketchModal from "./CollectionBuildSketchModal";
import {
  COLLECTION_GARMENT_FORMATS,
  CollectionBuildRangeBreakdown,
  EMPTY_SKETCH_ASSETS,
  GARMENT_FORMAT_MULTI_HELPER,
  collectionBuildInfluenceSelectOptions,
  SILHOUETTE_FIELD_LABELS,
  SILHOUETTE_HELPER,
  SILHOUETTE_STYLE_COLUMN_CLASS,
  denimCatalogLabelForId,
  fieldLabelClassName,
  isCollectionBuildForecastInfluence,
  isCollectionBuildLibraryInfluence,
  isRequiredSilhouetteFieldForContext,
  isSilhouetteStyleComplete,
  normalizeCollectionBuildInfluence,
  sectionBlockClassName,
  sectionHelperClassName,
  sectionTitleClassName,
  SilhouetteFieldKey,
  SilhouetteGroup,
  SilhouetteLookSeed,
  SilhouetteSketchAsset,
  SilhouetteStyleValues,
  areSilhouetteGroupsComplete,
  collectionBuildBorderClassName,
  collectionBuildDividerClassName,
  createSilhouetteGroups,
  garmentFormatRankLabel,
  garmentFormatSelectSummary,
  lengthOptionsForProduct,
  normalizeGarmentFormats,
  outlineButtonClassName,
  patternFormatsForGarment,
  silhouetteCatalogDirection,
  silhouetteFieldsFor,
  silhouetteProductPillLabel,
  silhouetteSketchCaption,
  submitButtonClassName,
  textareaClassName,
  toSelectOptions,
  toggleGarmentFormatSelection,
  updateGarmentFormatEntry,
  withSyncedGarmentFormatFields,
  applyLooksToSilhouetteGroups,
} from "./collectionBuildConstants";
import { RequiredMark } from "./ImageGeneratorControls";
import {
  PALETTE_EXTRACT_ACCEPT,
  paletteExtractFileError,
} from "./shared/generativeCatalog";

const SelectedSketchThumb: React.FC<{
  asset: SilhouetteSketchAsset;
  caption?: string | null;
  onRemove?: () => void;
}> = ({ asset, caption, onRemove }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;
    setSrc(null);
    (async () => {
      try {
        const preview = await resolveImageGeneratorResultSrc(asset.url);
        if (cancelled) {
          if (preview.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
          }
          return;
        }
        if (preview.startsWith("blob:")) {
          blobUrl = preview;
        }
        setSrc(preview);
      } catch {
        if (!cancelled) {
          setSrc(asset.url);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [asset.url]);

  return (
    <div className="relative flex w-[72px] shrink-0 flex-col gap-1 pt-1.5 pr-1.5">
      <div className="relative">
        <div className="flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-lg border border-[#E8DDD4] bg-white">
          {src ? (
            <img
              src={src}
              alt=""
              className="h-full w-full object-contain p-1"
            />
          ) : (
            <span className="px-1 text-center text-[9px] font-medium uppercase tracking-wide text-tertiary">
              Sketch
            </span>
          )}
        </div>
        {onRemove ? (
          <button
            type="button"
            aria-label="Remove silhouette"
            title="Remove"
            onClick={event => {
              event.stopPropagation();
              onRemove();
            }}
            className="absolute -right-1.5 -top-1.5 z-10 flex h-4 w-4 items-center justify-center rounded-full border border-[#737373] bg-white text-[11px] leading-none text-secondary shadow-sm hover:bg-[#F5F5F5] focus:outline-none"
          >
            ×
          </button>
        ) : null}
      </div>
      {caption ? (
        <p
          className="truncate text-center text-[11px] leading-[14px] text-secondary"
          title={caption}
        >
          {caption}
        </p>
      ) : null}
    </div>
  );
};

type Props = {
  breakdown: CollectionBuildRangeBreakdown[];
  category: string;
  market?: string;
  season?: string;
  seasonId?: string;
  startingDirection?: string;
  /** @deprecated Prefer live silhouettes-catalog fetch; kept as empty fallback. */
  sketchAssets?: SilhouetteSketchAsset[];
  sketchAssetsLoading?: boolean;
  onGenerateLook: (groups: SilhouetteGroup[]) => void;
  generating?: boolean;
  className?: string;
  /**
   * When true, collect Look Details for later moodboard/looks actions —
   * hide the terminal Generate Look CTA (query-form early placement).
   */
  deferredGenerate?: boolean;
  /** Keeps parent session state in sync while editing Look Details early. */
  onGroupsChange?: (groups: SilhouetteGroup[]) => void;
  /** Persisted look payloads to restore into silhouette fields on reopen. */
  seedLooks?: SilhouetteLookSeed[];
  /**
   * Legacy session-level Print/Embroidery when looks omit per-style values.
   * Applied once during hydrate into each style column.
   */
  sessionGarmentFallback?: {
    garmentFormat?: string;
    patternFormat?: string;
    embroideryType?: string;
  };
  /** When false (Denim), omit Garments Format / pattern / embroidery fields. */
  showGarmentFormat?: boolean;
  /** Denim Look Details Materials / Washes swatch catalogs (Figma multi-select). */
  denimMaterialOptions?: DenimSwatchOption[];
  denimWashOptions?: DenimSwatchOption[];
};

type SketchPickerTarget = {
  productType: string;
  index: number;
};

const EMPTY_SKETCH_IDS: string[] = [];

const ALL_PRODUCTS = "all";

const styleUploadKey = (productType: string, index: number) =>
  `${productType}:${index}`;

type ApplyStylePicker = {
  productType: string;
  sourceIndex: number;
};

const cloneSilhouetteStyleValues = (
  style: SilhouetteStyleValues
): SilhouetteStyleValues => ({
  ...style,
  garmentFormats: (style.garmentFormats || []).map(entry => ({ ...entry })),
  selectedSketchIds: [...(style.selectedSketchIds || [])],
});

const silhouetteFieldLabel = (
  field: SilhouetteFieldKey,
  isDenimPath: boolean
) => {
  if (isDenimPath && field === "wash") {
    return "Washes";
  }
  return SILHOUETTE_FIELD_LABELS[field];
};

const formatApplyStylesTriggerSummary = (
  indexes: number[],
  total: number
): string => {
  if (!indexes.length) {
    return "";
  }
  if (total > 0 && indexes.length === total) {
    return "All Styles";
  }
  const nums = [...indexes].sort((a, b) => a - b).map(index => index + 1);
  const isContiguous =
    nums.length > 1 &&
    nums.every((num, offset) => offset === 0 || num === nums[offset - 1] + 1);
  if (isContiguous) {
    return `Style ${nums[0]}..${nums[nums.length - 1]}`;
  }
  return nums.map(num => `Style ${num}`).join(", ");
};

const formatApplyStylesFooterHint = (indexes: number[]): string => {
  if (!indexes.length) {
    return "";
  }
  const nums = [...indexes].sort((a, b) => a - b).map(index => index + 1);
  return `*Apply to Style ${nums.join(", ")}`;
};

/** Same check glyph as Collection Build multi-look select. */
const ApplyStyleCheckboxIcon: React.FC = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden>
    <path
      d="M1 4.2 3.6 6.8 9 1.2"
      stroke="#FFFFFF"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ApplyStyleCheckbox: React.FC<{
  checked: boolean;
  disabled?: boolean;
  label: string;
  onToggle: () => void;
}> = ({ checked, disabled = false, label, onToggle }) => (
  <label
    className={`flex items-center gap-2 text-sm ${
      disabled
        ? "cursor-not-allowed text-tertiary opacity-40"
        : "cursor-pointer text-secondary"
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onToggle}
      className="sr-only"
    />
    <span
      aria-hidden
      className={`flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[2px] border ${
        checked
          ? "border-[#5A5A5A] bg-[#5A5A5A] text-white"
          : `${collectionBuildBorderClassName} bg-white`
      } ${disabled ? "bg-[#F5F5F5]" : ""}`}
    >
      {checked && !disabled ? <ApplyStyleCheckboxIcon /> : null}
    </span>
    {label}
  </label>
);

const applyStyleTriggerClassName = (enabled: boolean, hasValue: boolean) =>
  `box-border flex h-10 w-full items-center justify-between rounded-lg border ${collectionBuildBorderClassName} bg-white pl-3.5 pr-3.5 text-left font-avenir text-sm focus:outline-none ${
    !enabled
      ? "cursor-not-allowed text-tertiary opacity-40"
      : hasValue
        ? "cursor-pointer text-secondary hover:bg-[#F8F4F1]"
        : "cursor-pointer text-tertiary hover:bg-[#F8F4F1]"
  }`;

const productHeadingClassName = `mb-3 mt-8 ${sectionTitleClassName}`;
const groupTitleClassName = sectionTitleClassName;
const styleHeadingClassName = `mb-5 text-center ${sectionTitleClassName}`;
/** Keep CB silhouette labels on one line (rank suffixes must not wrap). */
const silhouetteLabelClassName = `${fieldLabelClassName} whitespace-nowrap`;

/** Match IG FieldLabel: label text + red * for required fields. */
const SilhouetteFieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
}> = ({ children, required = false }) => (
  <span className={silhouetteLabelClassName}>
    {children}
    {required ? <RequiredMark /> : null}
  </span>
);

const productPillClassName = (active: boolean) =>
  `h-[30px] rounded-lg border px-3 text-[14px] font-medium font-avenir focus:outline-none ${
    active
      ? "border-[#D3C0B6] bg-brandPrimarySubtle text-primary"
      : "border-[#E8DDD4] bg-[#F8F4F1] text-secondary hover:bg-[#F3EDE8]"
  }`;

/** Match CollectionBuildSelect silhouette trigger: h-10, rounded-lg; beige fill (not white). */
const trendButtonClassName = (active: boolean) =>
  `box-border flex h-10 w-full cursor-pointer items-center justify-center rounded-lg ${
    active ? "border-2" : "border"
  } px-3.5 font-avenir text-sm font-semibold text-primary focus:outline-none ${collectionBuildBorderClassName} bg-[#F6F3F0] hover:bg-[#F3EDE8]`;

const garmentFormatTriggerClassName = `box-border flex h-10 w-full items-center justify-between rounded-lg border ${collectionBuildBorderClassName} bg-white pl-3.5 pr-3.5 text-left font-avenir text-sm focus:outline-none`;

const garmentFormatCheckboxPanelClassName =
  "mt-1 rounded-lg border border-[#E8DDD4] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)]";

const garmentFormatCheckboxClassName = (checked: boolean) =>
  `flex h-4 w-4 shrink-0 items-center justify-center rounded-[2px] border ${
    checked
      ? "border-[#1A1A1A] bg-[#1A1A1A] text-white"
      : `${collectionBuildBorderClassName} bg-white`
  }`;

type GarmentFormatDropdownProps = {
  value: SilhouetteStyleValues["garmentFormats"];
  onChange: (next: SilhouetteStyleValues["garmentFormats"]) => void;
};

/** Closed-by-default multi-select; ranks follow check order (Primary → Tertiary). */
const GarmentFormatDropdown: React.FC<GarmentFormatDropdownProps> = ({
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="mt-1" ref={rootRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
        className={`${garmentFormatTriggerClassName} ${
          value.length > 0 ? "text-secondary" : "text-tertiary"
        }`}
      >
        <span className="truncate">{garmentFormatSelectSummary(value)}</span>
        <span
          className={`ml-2 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        >
          <DropdownArrow className="h-2 w-2 text-secondary" />
        </span>
      </button>
      {open ? (
        <div
          className={garmentFormatCheckboxPanelClassName}
          role="group"
          aria-label="Garments Format options"
        >
          <p className="px-3.5 pb-2.5 pt-3 text-[12px] font-normal leading-[16px] text-tertiary">
            {GARMENT_FORMAT_MULTI_HELPER}
          </p>
          <div
            className={`mx-3.5 border-t ${collectionBuildDividerClassName}`}
          />
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-3.5 py-3">
            {COLLECTION_GARMENT_FORMATS.map(format => {
              const entry = value.find(item => item.format === format);
              const checked = Boolean(entry);
              return (
                <label
                  key={format}
                  className="inline-flex cursor-pointer items-center gap-2 text-[13px] font-medium text-primary"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      onChange(
                        toggleGarmentFormatSelection(value, format, !checked)
                      );
                    }}
                    className="sr-only"
                  />
                  <span
                    aria-hidden
                    className={garmentFormatCheckboxClassName(checked)}
                  >
                    {checked ? (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 4.2 3.6 6.8 9 1.2"
                          stroke="#FFFFFF"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                  </span>
                  <span className="whitespace-nowrap">
                    {format}
                    {entry ? (
                      <span className="font-normal text-tertiary">
                        {` (${garmentFormatRankLabel(entry.rank)})`}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const productTypeMatchTokens = (productType: string): string[] => {
  const raw = productType.trim().toLowerCase();
  if (!raw) {
    return [];
  }
  const tokens = new Set<string>([raw]);
  if (raw.endsWith("ses") && raw.length > 4) {
    tokens.add(raw.slice(0, -2));
  } else if (raw.endsWith("s") && raw.length > 3 && !raw.endsWith("ss")) {
    tokens.add(raw.slice(0, -1));
  }
  if (raw.includes("jean")) {
    tokens.add("jeans");
    tokens.add("denim");
  }
  if (raw.includes("kurta")) {
    tokens.add("kurta");
    tokens.add("kurtas");
  }
  return Array.from(tokens);
};

const assetMatchesProductType = (
  asset: SilhouetteSketchAsset,
  productType: string
) => {
  const blob = `${asset.label || ""} ${asset.stem}`.toLowerCase();
  return productTypeMatchTokens(productType).some(token =>
    blob.includes(token)
  );
};

const optionsForField = (
  field: SilhouetteFieldKey,
  productType: string,
  market: string
) => {
  switch (field) {
    case "influence":
      return collectionBuildInfluenceSelectOptions();
    case "styleType":
      return toSelectOptions(styleOptionsFor(market, productType));
    case "sleeve":
      return toSelectOptions(SLEEVE_OPTIONS);
    case "neckline":
      return toSelectOptions(NECKLINE_OPTIONS);
    case "materials":
      return toSelectOptions(materialsOptionsFor(productType));
    case "length":
      return toSelectOptions(lengthOptionsForProduct(productType));
    case "fit":
      return toSelectOptions(FIT_OPTIONS);
    case "wash":
      return toSelectOptions(WASH_OPTIONS);
    default:
      return [];
  }
};

const styleColumnPad = (index: number, count: number) => {
  if (count <= 1 || index === 0) {
    return "";
  }
  return `border-l ${collectionBuildDividerClassName} pl-4`;
};

const CollectionBuildSilhouette = React.forwardRef<HTMLElement, Props>(
  (
    {
      breakdown,
      category,
      market = "",
      season = "",
      seasonId = "",
      startingDirection = "",
      sketchAssets: sketchAssetsFallback = EMPTY_SKETCH_ASSETS,
      sketchAssetsLoading: sketchAssetsLoadingFallback = false,
      onGenerateLook,
      generating = false,
      className,
      deferredGenerate = false,
      onGroupsChange,
      seedLooks,
      sessionGarmentFallback,
      showGarmentFormat = false,
      denimMaterialOptions = [],
      denimWashOptions = [],
    },
    ref
  ) => {
    const [groups, setGroups] = useState<SilhouetteGroup[]>(() =>
      applyLooksToSilhouetteGroups(
        createSilhouetteGroups(breakdown),
        seedLooks,
        sessionGarmentFallback
      )
    );
    const [productFilter, setProductFilter] = useState(ALL_PRODUCTS);
    const breakdownKey = breakdown
      .map(item => `${item.productType}:${item.quantity}`)
      .join("|");
    // Identity for reseed only — omit garment/pattern/embroidery so format edits
    // (and parent sessionGarmentFallback sync) do not wipe filled style details.
    const seedLooksKey = (seedLooks || [])
      .map(
        look =>
          `${look.productType || ""}:${look.styleIndex ?? ""}:${look.influence || ""}:${look.styleType || ""}:${look.selectedSketchStems?.join(",") || ""}:${look.sketchUploadName || ""}:${look.sketchUploadUrl || ""}`
      )
      .join("|");
    useEffect(() => {
      setGroups(
        applyLooksToSilhouetteGroups(
          createSilhouetteGroups(breakdown),
          seedLooks,
          sessionGarmentFallback
        )
      );
      setProductFilter(ALL_PRODUCTS);
      setApplyPicker(null);
      setApplyDraftIndexes([]);
      setApplySummaryByKey({});
      // Reseed on breakdown / seed look identity only — not session garment fallback
      // (parent syncs that from these groups when the user picks a format).
    }, [breakdownKey, seedLooksKey]);
    useEffect(() => {
      onGroupsChange?.(groups);
    }, [groups, onGroupsChange]);
    const [sketchPicker, setSketchPicker] = useState<SketchPickerTarget | null>(
      null
    );
    const [applyPicker, setApplyPicker] = useState<ApplyStylePicker | null>(
      null
    );
    const [applyDraftIndexes, setApplyDraftIndexes] = useState<number[]>([]);
    const [applySummaryByKey, setApplySummaryByKey] = useState<
      Record<string, string>
    >({});
    const [catalogAssets, setCatalogAssets] =
      useState<SilhouetteSketchAsset[]>(EMPTY_SKETCH_ASSETS);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogFetched, setCatalogFetched] = useState(false);
    const [uploadPreviews, setUploadPreviews] = useState<
      Record<string, string>
    >({});
    const [uploadingKey, setUploadingKey] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadTargetRef = useRef<SketchPickerTarget | null>(null);
    const uploadPreviewsRef = useRef(uploadPreviews);
    uploadPreviewsRef.current = uploadPreviews;
    const fieldContext = useMemo(
      () => ({ category, market, startingDirection }),
      [category, market, startingDirection]
    );
    const canGenerateLook = useMemo(
      () => areSilhouetteGroupsComplete(groups, fieldContext),
      [groups, fieldContext]
    );

    useEffect(
      () => () => {
        Array.from(new Set(Object.values(uploadPreviewsRef.current))).forEach(
          url => {
            URL.revokeObjectURL(url);
          }
        );
      },
      []
    );

    const productTypes = useMemo(
      () =>
        breakdown
          .map(item => item.productType)
          .filter(Boolean)
          .filter((value, index, list) => list.indexOf(value) === index),
      [breakdown]
    );
    const productTypesKey = productTypes.join("|");

    useEffect(() => {
      const seasonLabel = season.trim();
      const marketLabel = market.trim();
      const categoryLabel = category.trim();
      if (!seasonLabel || !marketLabel || !categoryLabel) {
        setCatalogAssets(EMPTY_SKETCH_ASSETS);
        setCatalogLoading(false);
        setCatalogFetched(false);
        return;
      }
      let cancelled = false;
      setCatalogLoading(true);
      getCollectionBuildSilhouettesCatalog({
        season: seasonLabel,
        seasonId: seasonId || undefined,
        market: marketLabel,
        category: categoryLabel,
        startingDirection: startingDirection || undefined,
        productTypes: productTypesKey ? productTypesKey.split("|") : undefined,
      })
        .then(catalog => {
          if (cancelled) {
            return;
          }
          const next = (catalog?.silhouettes || [])
            .filter(slide => slide.image?.available && slide.image?.url)
            .map(
              (slide): SilhouetteSketchAsset => ({
                stem: slide.id,
                url: slide.image.url,
                available: true,
                label: slide.name,
                reportTitle: slide.reportTitle || undefined,
                sliderImageIndex: slide.sliderImageIndex,
              })
            );
          setCatalogAssets(next);
          setCatalogFetched(true);
        })
        .catch(error => {
          if (cancelled) {
            return;
          }
          console.warn(
            getCollectionBuildErrorMessage(
              error,
              "No silhouettes for this season, market, and category."
            )
          );
          setCatalogAssets(EMPTY_SKETCH_ASSETS);
          setCatalogFetched(true);
        })
        .finally(() => {
          if (!cancelled) {
            setCatalogLoading(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }, [
      season,
      seasonId,
      market,
      category,
      startingDirection,
      productTypesKey,
    ]);

    const sketchAssets = catalogFetched
      ? catalogAssets
      : catalogAssets.length > 0
        ? catalogAssets
        : sketchAssetsFallback;
    const sketchAssetsLoading =
      catalogLoading ||
      (sketchAssetsLoadingFallback && sketchAssets.length === 0);

    const modalAssets = useMemo(() => {
      const libraryAssets = catalogFetched ? catalogAssets : sketchAssets;
      if (!sketchPicker) {
        return libraryAssets;
      }
      const matched = libraryAssets.filter(asset =>
        assetMatchesProductType(asset, sketchPicker.productType)
      );
      return matched.length > 0 ? matched : libraryAssets;
    }, [catalogAssets, catalogFetched, sketchAssets, sketchPicker]);

    const visibleGroups = useMemo(
      () =>
        productFilter === ALL_PRODUCTS
          ? groups
          : groups.filter(group => group.productType === productFilter),
      [groups, productFilter]
    );

    const updateStyle = (
      productType: string,
      index: number,
      patch: Partial<SilhouetteStyleValues>
    ) => {
      setGroups(prev =>
        prev.map(group =>
          group.productType !== productType
            ? group
            : {
                ...group,
                styles: group.styles.map((style, styleIndex) => {
                  if (styleIndex !== index) {
                    return style;
                  }
                  let next: SilhouetteStyleValues = { ...style, ...patch };
                  if (patch.garmentFormats !== undefined) {
                    next = withSyncedGarmentFormatFields({
                      ...next,
                      garmentFormats: normalizeGarmentFormats(
                        patch.garmentFormats
                      ),
                    });
                  } else if (
                    patch.garmentFormat !== undefined ||
                    patch.patternFormat !== undefined ||
                    patch.embroideryType !== undefined
                  ) {
                    // Legacy single-field patches — do not wipe sibling formats.
                    next = withSyncedGarmentFormatFields(next);
                  }
                  if (patch.styleType === undefined) {
                    return next;
                  }
                  const allowed = new Set(
                    silhouetteFieldsFor(productType, fieldContext)
                  );
                  const keepMaterials = allowed.has("materials");
                  const keepWash = allowed.has("wash");
                  return {
                    ...next,
                    sleeve: allowed.has("sleeve") ? next.sleeve : "",
                    neckline: allowed.has("neckline") ? next.neckline : "",
                    materials: keepMaterials ? next.materials : "",
                    length: allowed.has("length") ? next.length : "",
                    fit: allowed.has("fit") ? next.fit : "",
                    wash: keepWash ? next.wash : "",
                    denimMaterialId: keepMaterials
                      ? next.denimMaterialId || ""
                      : "",
                    denimWashId: keepWash ? next.denimWashId || "" : "",
                  };
                }),
              }
        )
      );
    };

    const isDenimPath =
      silhouetteCatalogDirection(startingDirection) === "denim";

    const setGarmentFormatsForStyle = (
      productType: string,
      index: number,
      nextFormats: SilhouetteStyleValues["garmentFormats"]
    ) => {
      updateStyle(productType, index, {
        garmentFormats: normalizeGarmentFormats(nextFormats),
      });
    };

    const clearUploadPreview = (productType: string, index: number) => {
      const key = styleUploadKey(productType, index);
      setUploadPreviews(prev => {
        const existing = prev[key];
        if (!existing) {
          return prev;
        }
        const next = { ...prev };
        delete next[key];
        const stillUsed = Object.values(next).includes(existing);
        if (!stillUsed) {
          URL.revokeObjectURL(existing);
        }
        return next;
      });
    };

    const openApplyStylePicker = (productType: string, sourceIndex: number) => {
      const group = groups.find(item => item.productType === productType);
      if (!group) {
        return;
      }
      const source = group.styles[sourceIndex];
      if (
        !source ||
        group.styles.length <= 1 ||
        !isSilhouetteStyleComplete(source, productType, fieldContext)
      ) {
        return;
      }
      setApplyPicker({ productType, sourceIndex });
      setApplyDraftIndexes([]);
    };

    const closeApplyStylePicker = () => {
      setApplyPicker(null);
      setApplyDraftIndexes([]);
    };

    const confirmApplyStylePicker = () => {
      if (!applyPicker || !applyDraftIndexes.length) {
        return;
      }
      const { productType, sourceIndex } = applyPicker;
      const sourceKey = styleUploadKey(productType, sourceIndex);
      const sourcePreview = uploadPreviewsRef.current[sourceKey];
      const targetIndexes = Array.from(new Set(applyDraftIndexes))
        .filter(index => index !== sourceIndex)
        .sort((a, b) => a - b);
      if (!targetIndexes.length) {
        return;
      }
      const group = groups.find(item => item.productType === productType);
      const selectableTotal = Math.max((group?.styles.length || 0) - 1, 0);

      setGroups(prev =>
        prev.map(item => {
          if (item.productType !== productType) {
            return item;
          }
          const source = item.styles[sourceIndex];
          if (!source) {
            return item;
          }
          return {
            ...item,
            styles: item.styles.map((style, styleIndex) =>
              targetIndexes.includes(styleIndex)
                ? cloneSilhouetteStyleValues(source)
                : style
            ),
          };
        })
      );

      setUploadPreviews(prev => {
        const next = { ...prev };
        targetIndexes.forEach(targetIndex => {
          if (targetIndex === sourceIndex) {
            return;
          }
          const key = styleUploadKey(productType, targetIndex);
          const existing = next[key];
          if (sourcePreview) {
            if (existing && existing !== sourcePreview) {
              const remaining = { ...next };
              delete remaining[key];
              if (!Object.values(remaining).includes(existing)) {
                URL.revokeObjectURL(existing);
              }
            }
            next[key] = sourcePreview;
            return;
          }
          if (existing) {
            delete next[key];
            if (!Object.values(next).includes(existing)) {
              URL.revokeObjectURL(existing);
            }
          }
        });
        return next;
      });

      setApplySummaryByKey(prev => ({
        ...prev,
        [sourceKey]: formatApplyStylesTriggerSummary(
          targetIndexes,
          selectableTotal
        ),
      }));
      closeApplyStylePicker();
    };

    const openFilePicker = (productType: string, index: number) => {
      uploadTargetRef.current = { productType, index };
      fileInputRef.current?.click();
    };

    const handleSilhouetteUpload = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      const target = uploadTargetRef.current;
      uploadTargetRef.current = null;
      if (!file || !target) {
        return;
      }
      const fileError = paletteExtractFileError(file);
      if (fileError) {
        toast.error(fileError);
        return;
      }

      const key = styleUploadKey(target.productType, target.index);
      const objectUrl = URL.createObjectURL(file);
      setUploadPreviews(prev => {
        const existing = prev[key];
        if (existing) {
          URL.revokeObjectURL(existing);
        }
        return { ...prev, [key]: objectUrl };
      });
      updateStyle(target.productType, target.index, {
        uploadFileName: file.name,
        uploadUrl: "",
        selectedSketchIds: [],
        silhouetteId: "",
        silhouetteName: "",
      });

      setUploadingKey(key);
      try {
        const s3FileName = getFileName(file);
        const uploaded = await uploadImage(
          getKeyForS3GenerativeFeature(s3FileName),
          file
        );
        const publicUrl = String(
          getImageLink(uploaded.url) || uploaded.url || ""
        ).trim();
        if (!publicUrl) {
          throw new Error(
            "Upload succeeded but no public image URL was returned."
          );
        }
        updateStyle(target.productType, target.index, {
          uploadFileName: file.name,
          uploadUrl: publicUrl,
          selectedSketchIds: [],
          silhouetteId: "",
          silhouetteName: "",
        });
      } catch (error) {
        console.warn(
          getCollectionBuildErrorMessage(
            error,
            "Could not upload silhouette image."
          )
        );
        // Keep filename so incomplete-upload gate can block generate.
        updateStyle(target.productType, target.index, {
          uploadFileName: file.name,
          uploadUrl: "",
        });
        toast.error(
          "Preview saved locally, but the image could not be uploaded. Re-upload before generating."
        );
      } finally {
        setUploadingKey(null);
      }
    };

    const sketchPickerStyle =
      sketchPicker &&
      groups.find(group => group.productType === sketchPicker.productType)
        ?.styles[sketchPicker.index];

    const applyPickerGroup = applyPicker
      ? groups.find(group => group.productType === applyPicker.productType)
      : undefined;
    const applyPickerSelectableIndexes =
      applyPicker && applyPickerGroup
        ? applyPickerGroup.styles
            .map((_, index) => index)
            .filter(index => index !== applyPicker.sourceIndex)
        : [];
    const applyPickerAllSelected =
      applyPickerSelectableIndexes.length > 0 &&
      applyPickerSelectableIndexes.every(index =>
        applyDraftIndexes.includes(index)
      );

    return (
      <section
        ref={ref}
        className={`min-w-0 max-w-full ${className ?? sectionBlockClassName}`}
      >
        <h2 className={sectionTitleClassName}>Silhouette</h2>
        <p className={sectionHelperClassName}>{SILHOUETTE_HELPER}</p>

        <h3 className={productHeadingClassName}>Select Product</h3>
        <div className="mb-8 flex min-w-0 max-w-full flex-wrap items-center gap-[13px]">
          <button
            type="button"
            onClick={() => setProductFilter(ALL_PRODUCTS)}
            className={productPillClassName(productFilter === ALL_PRODUCTS)}
          >
            All
          </button>
          {groups.map(group => (
            <button
              key={group.productType}
              type="button"
              onClick={() => setProductFilter(group.productType)}
              className={productPillClassName(
                productFilter === group.productType
              )}
            >
              {silhouetteProductPillLabel(
                group.productType,
                group.styles.length
              )}
            </button>
          ))}
        </div>

        <div className="flex min-w-0 flex-col items-start gap-8">
          {visibleGroups.map(group => {
            const styleCount = group.styles.length;
            return (
              <article
                key={group.productType}
                className="w-fit min-w-0 max-w-full rounded-[16px] border border-light bg-white px-6 py-6"
              >
                <div
                  className={`mb-6 border-b ${collectionBuildDividerClassName} pb-3`}
                >
                  <h3 className={groupTitleClassName}>{group.productType}</h3>
                </div>
                <div className="min-w-0 max-w-full overflow-x-auto">
                  <div className="flex w-max min-w-0 flex-row items-start gap-x-4">
                    {group.styles.map((style, index) => {
                      const fields = silhouetteFieldsFor(
                        group.productType,
                        fieldContext
                      );
                      const openSketchPicker = () =>
                        setSketchPicker({
                          productType: group.productType,
                          index,
                        });
                      const uploadKey = styleUploadKey(
                        group.productType,
                        index
                      );
                      const uploadPreview = uploadPreviews[uploadKey];
                      const isUploading = uploadingKey === uploadKey;
                      const hasUpload = Boolean(
                        style.uploadFileName?.trim() || uploadPreview
                      );
                      const selectedSketchAssets = style.selectedSketchIds
                        .map(id =>
                          sketchAssets.find(asset => asset.stem === id)
                        )
                        .filter((asset): asset is SilhouetteSketchAsset =>
                          Boolean(asset)
                        );
                      const hasSketches = style.selectedSketchIds.length > 0;
                      const removeSelectedSketch = (stem: string) => {
                        const nextIds = style.selectedSketchIds.filter(
                          id => id !== stem
                        );
                        const primary = nextIds[0] || "";
                        const primaryAsset = sketchAssets.find(
                          item => item.stem === primary
                        );
                        updateStyle(group.productType, index, {
                          selectedSketchIds: nextIds,
                          silhouetteId: primary,
                          silhouetteName: primaryAsset?.label || "",
                        });
                      };
                      const garmentFormats = normalizeGarmentFormats(
                        style.garmentFormats,
                        {
                          garmentFormat: style.garmentFormat,
                          patternFormat: style.patternFormat,
                          embroideryType: style.embroideryType,
                        }
                      );
                      const embroideryEntries = garmentFormats.filter(
                        entry =>
                          entry.format === "Embroidery" &&
                          Boolean(entry.patternFormat?.trim())
                      );
                      const influenceValue = normalizeCollectionBuildInfluence(
                        style.influence
                      );
                      const canApplyStyle =
                        styleCount > 1 &&
                        isSilhouetteStyleComplete(
                          style,
                          group.productType,
                          fieldContext
                        );
                      const applySummary = applySummaryByKey[uploadKey] || "";
                      return (
                        <React.Fragment
                          key={`${group.productType}-style-${index}`}
                        >
                          <div
                            className={`${SILHOUETTE_STYLE_COLUMN_CLASS} ${styleColumnPad(
                              index,
                              styleCount
                            )}`}
                          >
                            <p className={styleHeadingClassName}>
                              Style {index + 1}
                            </p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                              {fields.map(field => {
                                const useDenimSwatch =
                                  isDenimPath &&
                                  (field === "materials" || field === "wash");
                                return (
                                  <React.Fragment key={field}>
                                    <div className="min-w-0">
                                      <SilhouetteFieldLabel
                                        required={isRequiredSilhouetteFieldForContext(
                                          field,
                                          style.influence,
                                          fieldContext
                                        )}
                                      >
                                        {silhouetteFieldLabel(
                                          field,
                                          isDenimPath
                                        )}
                                      </SilhouetteFieldLabel>
                                      {useDenimSwatch ? (
                                        <CollectionBuildDenimSwatchSelect
                                          variant={
                                            field === "materials"
                                              ? "materials"
                                              : "washes"
                                          }
                                          value={
                                            field === "materials"
                                              ? style.denimMaterialId || ""
                                              : style.denimWashId || ""
                                          }
                                          options={
                                            field === "materials"
                                              ? denimMaterialOptions
                                              : denimWashOptions
                                          }
                                          onChange={nextId => {
                                            if (field === "materials") {
                                              updateStyle(
                                                group.productType,
                                                index,
                                                {
                                                  denimMaterialId: nextId,
                                                  materials:
                                                    denimCatalogLabelForId(
                                                      nextId,
                                                      denimMaterialOptions
                                                    ),
                                                }
                                              );
                                              return;
                                            }
                                            updateStyle(
                                              group.productType,
                                              index,
                                              {
                                                denimWashId: nextId,
                                                wash: denimCatalogLabelForId(
                                                  nextId,
                                                  denimWashOptions
                                                ),
                                              }
                                            );
                                          }}
                                        />
                                      ) : (
                                        <CollectionBuildSelect
                                          value={
                                            field === "influence"
                                              ? influenceValue
                                              : String(style[field] || "")
                                          }
                                          placeholder="Select"
                                          variant="silhouette"
                                          searchable={field === "materials"}
                                          searchPlaceholder="Search material"
                                          options={optionsForField(
                                            field,
                                            group.productType,
                                            market
                                          )}
                                          onChange={value => {
                                            if (field === "influence") {
                                              const nextInfluence =
                                                normalizeCollectionBuildInfluence(
                                                  value
                                                );
                                              const isLibrary =
                                                isCollectionBuildLibraryInfluence(
                                                  nextInfluence
                                                );
                                              const isForecast =
                                                isCollectionBuildForecastInfluence(
                                                  nextInfluence
                                                );
                                              const patch: Partial<SilhouetteStyleValues> =
                                                {
                                                  influence: nextInfluence,
                                                };
                                              if (isLibrary) {
                                                patch.selectedSketchIds = [];
                                                patch.silhouetteId = "";
                                                patch.silhouetteName = "";
                                                if (
                                                  sketchPicker?.productType ===
                                                    group.productType &&
                                                  sketchPicker.index === index
                                                ) {
                                                  setSketchPicker(null);
                                                }
                                              } else if (isForecast) {
                                                clearUploadPreview(
                                                  group.productType,
                                                  index
                                                );
                                                patch.uploadFileName = "";
                                                patch.uploadUrl = "";
                                                setSketchPicker({
                                                  productType:
                                                    group.productType,
                                                  index,
                                                });
                                              } else {
                                                clearUploadPreview(
                                                  group.productType,
                                                  index
                                                );
                                                patch.uploadFileName = "";
                                                patch.uploadUrl = "";
                                                patch.selectedSketchIds = [];
                                                patch.silhouetteId = "";
                                                patch.silhouetteName = "";
                                                if (
                                                  sketchPicker?.productType ===
                                                    group.productType &&
                                                  sketchPicker.index === index
                                                ) {
                                                  setSketchPicker(null);
                                                }
                                              }
                                              updateStyle(
                                                group.productType,
                                                index,
                                                patch
                                              );
                                              return;
                                            }
                                            updateStyle(
                                              group.productType,
                                              index,
                                              {
                                                [field]: value,
                                              }
                                            );
                                          }}
                                        />
                                      )}
                                    </div>
                                  </React.Fragment>
                                );
                              })}
                            </div>
                            {showGarmentFormat ? (
                              <div className="mt-4 flex min-w-0 flex-col">
                                <SilhouetteFieldLabel>
                                  Select Garments Format
                                </SilhouetteFieldLabel>
                                <GarmentFormatDropdown
                                  value={garmentFormats}
                                  onChange={next =>
                                    setGarmentFormatsForStyle(
                                      group.productType,
                                      index,
                                      next
                                    )
                                  }
                                />
                                {garmentFormats.length > 0 ? (
                                  <div className="mt-3 grid min-w-0 grid-cols-3 gap-2">
                                    {garmentFormats.map(entry => (
                                      <div
                                        key={entry.format}
                                        className="min-w-0"
                                      >
                                        <span
                                          className={`${fieldLabelClassName} min-w-0 leading-snug`}
                                        >
                                          <span className="block min-w-0 truncate">
                                            {entry.format}
                                          </span>
                                          <span className="block whitespace-nowrap">
                                            {`(${garmentFormatRankLabel(entry.rank)})`}
                                          </span>
                                        </span>
                                        <div className="min-w-0">
                                          <CollectionBuildSelect
                                            value={entry.patternFormat}
                                            placeholder="Select"
                                            variant="silhouette"
                                            options={toSelectOptions(
                                              patternFormatsForGarment(
                                                entry.format
                                              )
                                            )}
                                            onChange={value =>
                                              setGarmentFormatsForStyle(
                                                group.productType,
                                                index,
                                                updateGarmentFormatEntry(
                                                  garmentFormats,
                                                  entry.format,
                                                  { patternFormat: value }
                                                )
                                              )
                                            }
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                                {embroideryEntries.map(entry => (
                                  <div
                                    key={`embroidery-${entry.format}`}
                                    className="mt-3 min-w-0"
                                  >
                                    <SilhouetteFieldLabel>
                                      Embroidery Stitch
                                    </SilhouetteFieldLabel>
                                    <CollectionBuildEmbroideryType
                                      value={entry.embroideryType}
                                      onChange={value =>
                                        setGarmentFormatsForStyle(
                                          group.productType,
                                          index,
                                          updateGarmentFormatEntry(
                                            garmentFormats,
                                            entry.format,
                                            { embroideryType: value }
                                          )
                                        )
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            ) : null}
                            {isCollectionBuildLibraryInfluence(
                              influenceValue
                            ) ? (
                              <div className="mt-5 min-w-0">
                                <span className={silhouetteLabelClassName}>
                                  Choose Trend
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    openFilePicker(group.productType, index)
                                  }
                                  disabled={isUploading}
                                  aria-pressed={hasUpload}
                                  className={`${trendButtonClassName(
                                    hasUpload
                                  )} disabled:cursor-wait disabled:opacity-60`}
                                >
                                  <span className="max-w-full truncate">
                                    {isUploading ? "Uploading..." : "Upload"}
                                  </span>
                                </button>
                                {hasUpload ? (
                                  <div className="mt-3 overflow-hidden rounded-xl border border-[#E8DDD4] bg-[#F8F4F1] p-3">
                                    <p className="mb-2 text-[12px] font-medium text-secondary">
                                      {isUploading
                                        ? `Uploading · ${style.uploadFileName || "image"}`
                                        : style.uploadUrl?.trim()
                                          ? style.uploadFileName
                                            ? `Uploaded · ${style.uploadFileName}`
                                            : "Uploaded image"
                                          : style.uploadFileName
                                            ? `Upload incomplete · ${style.uploadFileName}`
                                            : "Upload incomplete"}
                                    </p>
                                    {!isUploading &&
                                    style.uploadFileName?.trim() &&
                                    !style.uploadUrl?.trim() ? (
                                      <p className="mb-2 text-[11px] text-[#B42318]">
                                        Re-upload before generating — the image
                                        URL is missing.
                                      </p>
                                    ) : null}
                                    {uploadPreview ? (
                                      <img
                                        src={uploadPreview}
                                        alt={
                                          style.uploadFileName ||
                                          "Uploaded silhouette"
                                        }
                                        className="mx-auto max-h-40 w-full object-contain"
                                      />
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            ) : isCollectionBuildForecastInfluence(
                                influenceValue
                              ) ? (
                              <div className="mt-5 min-w-0">
                                <span className={silhouetteLabelClassName}>
                                  Choose Trend
                                </span>
                                <button
                                  type="button"
                                  onClick={openSketchPicker}
                                  aria-pressed={hasSketches}
                                  className={trendButtonClassName(hasSketches)}
                                >
                                  <span className="max-w-full truncate">
                                    Specific Sketch Only
                                  </span>
                                </button>
                                {selectedSketchAssets.length > 0 ? (
                                  <div className="mt-3 flex gap-2 overflow-x-auto">
                                    {selectedSketchAssets.map(
                                      (asset, sketchIndex) => (
                                        <SelectedSketchThumb
                                          key={asset.stem}
                                          asset={asset}
                                          caption={silhouetteSketchCaption(
                                            asset,
                                            sketchIndex,
                                            selectedSketchAssets.length
                                          )}
                                          onRemove={() =>
                                            removeSelectedSketch(asset.stem)
                                          }
                                        />
                                      )
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                            <div className="mt-5 min-w-0">
                              <span className={silhouetteLabelClassName}>
                                Additional Input (optional)
                              </span>
                              <textarea
                                value={style.additionalPrompt || ""}
                                placeholder="Input Text"
                                rows={4}
                                className={textareaClassName}
                                onChange={event =>
                                  updateStyle(group.productType, index, {
                                    additionalPrompt: event.target.value,
                                  })
                                }
                              />
                            </div>
                            <div className="mt-5 min-w-0">
                              <span className={silhouetteLabelClassName}>
                                {`Apply style ${index + 1} to`}
                              </span>
                              <button
                                type="button"
                                disabled={!canApplyStyle}
                                aria-haspopup="dialog"
                                aria-expanded={
                                  applyPicker?.productType ===
                                    group.productType &&
                                  applyPicker.sourceIndex === index
                                }
                                onClick={() =>
                                  openApplyStylePicker(group.productType, index)
                                }
                                className={applyStyleTriggerClassName(
                                  canApplyStyle,
                                  Boolean(applySummary)
                                )}
                              >
                                <span className="truncate">
                                  {applySummary || "Select"}
                                </span>
                                <span className="ml-2 shrink-0">
                                  <DropdownArrow className="h-2 w-2 text-secondary" />
                                </span>
                              </button>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {!deferredGenerate ? (
          <div className="mt-8 flex w-full justify-end">
            <button
              type="button"
              disabled={!canGenerateLook || generating}
              onClick={() => onGenerateLook(groups)}
              className={
                canGenerateLook && !generating
                  ? submitButtonClassName
                  : `${outlineButtonClassName} cursor-not-allowed opacity-40`
              }
            >
              Generate Look
            </button>
          </div>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept={PALETTE_EXTRACT_ACCEPT}
          className="hidden"
          onChange={handleSilhouetteUpload}
        />
        {applyPicker &&
          applyPickerGroup &&
          createPortal(
            <div className="fixed inset-0 z-[60] flex items-center justify-center font-avenir">
              <div
                className="fixed inset-0 bg-black/20"
                onClick={closeApplyStylePicker}
              />
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="apply-style-modal-title"
                className={`relative z-10 flex max-h-[min(36rem,calc(100vh-2rem))] w-[min(34rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-[22px] border ${collectionBuildBorderClassName} bg-white shadow-[0_8px_28px_rgba(0,0,0,0.12)]`}
              >
                <div
                  className={`flex items-center justify-between gap-3 border-b ${collectionBuildDividerClassName} px-6 py-4`}
                >
                  <h2
                    id="apply-style-modal-title"
                    className="text-[15px] font-medium text-secondary"
                  >
                    {`Apply Style ${applyPicker.sourceIndex + 1} to...`}
                  </h2>
                  <button
                    type="button"
                    onClick={() =>
                      setApplyDraftIndexes(
                        applyPickerAllSelected
                          ? []
                          : applyPickerSelectableIndexes
                      )
                    }
                    className={`h-8 shrink-0 rounded-full border ${collectionBuildBorderClassName} bg-white px-4 font-avenir text-sm text-secondary hover:bg-lightGray focus:outline-none`}
                  >
                    Select All
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                  <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                    {applyPickerGroup.styles.map((_, styleIndex) => {
                      const isSource = styleIndex === applyPicker.sourceIndex;
                      return (
                        <ApplyStyleCheckbox
                          key={`${applyPicker.productType}-apply-${styleIndex}`}
                          checked={
                            !isSource && applyDraftIndexes.includes(styleIndex)
                          }
                          disabled={isSource}
                          label={`Style ${styleIndex + 1}`}
                          onToggle={() => {
                            if (isSource) {
                              return;
                            }
                            setApplyDraftIndexes(prev =>
                              prev.includes(styleIndex)
                                ? prev.filter(index => index !== styleIndex)
                                : [...prev, styleIndex]
                            );
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
                <div
                  className={`flex items-center justify-between gap-3 border-t ${collectionBuildDividerClassName} px-6 py-4`}
                >
                  <p className="min-h-[1rem] text-xs text-tertiary">
                    {formatApplyStylesFooterHint(
                      applyDraftIndexes.filter(
                        index => index !== applyPicker.sourceIndex
                      )
                    )}
                  </p>
                  <button
                    type="button"
                    disabled={
                      !applyDraftIndexes.some(
                        index => index !== applyPicker.sourceIndex
                      )
                    }
                    onClick={confirmApplyStylePicker}
                    className="h-[46px] shrink-0 rounded-full bg-black px-8 font-avenir text-sm text-white hover:bg-[#111] focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

        <CollectionBuildSketchModal
          open={Boolean(sketchPicker)}
          assets={modalAssets}
          loading={sketchAssetsLoading}
          selectedIds={sketchPickerStyle?.selectedSketchIds || EMPTY_SKETCH_IDS}
          onClose={() => setSketchPicker(null)}
          onSelect={ids => {
            if (!sketchPicker) {
              return;
            }
            const primary = ids[0] || "";
            const primaryAsset = sketchAssets.find(
              item => item.stem === primary
            );
            clearUploadPreview(sketchPicker.productType, sketchPicker.index);
            updateStyle(sketchPicker.productType, sketchPicker.index, {
              selectedSketchIds: ids,
              silhouetteId: primary,
              silhouetteName: primaryAsset?.label || "",
              ...(ids.length > 0 ? { uploadFileName: "", uploadUrl: "" } : {}),
            });
            setSketchPicker(null);
          }}
        />
      </section>
    );
  }
);

CollectionBuildSilhouette.displayName = "CollectionBuildSilhouette";

export default CollectionBuildSilhouette;
